import { BRAND, type ColorMode } from '@chukua/common';
import { theme as antTheme, type ThemeConfig } from 'antd';

/**
 * Ant's half of the theme: the same BRAND palette Tailwind reads (brand-css.ts),
 * mapped onto Ant tokens for the data widgets (tables, forms, pickers). A second
 * company's branding is a new BRAND token set in @chukua/common, not a restyle
 * (docs/technical-design.md → Theming).
 */
/** `amount` (0–1) of `fg` blended over `bg`, both `#RRGGBB`. */
function mix(fg: string, bg: string, amount: number): string {
  const ch = (hex: string, i: number) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16);
  const out = [0, 1, 2].map((i) => Math.round(ch(fg, i) * amount + ch(bg, i) * (1 - amount)));
  return '#' + out.map((v) => v.toString(16).padStart(2, '0')).join('');
}

export function buildTheme(mode: ColorMode): ThemeConfig {
  const c = BRAND.palettes[mode];
  // Ant derives status backgrounds/borders (Alert, Tag, Form status) from the
  // seed colour; with muted earth tones that comes out muddy in light and
  // near-invisible in dark. Tint the surface with the status colour instead.
  const tint = mode === 'dark' ? 0.14 : 0.1;
  const edge = mode === 'dark' ? 0.35 : 0.3;
  const status = (name: 'Success' | 'Warning' | 'Error' | 'Info', color: string) => ({
    [`color${name}Bg`]: mix(color, c.surface, tint),
    [`color${name}BgHover`]: mix(color, c.surface, tint + 0.06),
    [`color${name}Border`]: mix(color, c.surface, edge),
    [`color${name}BorderHover`]: mix(color, c.surface, edge + 0.15),
  });
  return {
    algorithm: mode === 'dark' ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    token: {
      colorPrimary: c.primary,
      colorSuccess: c.success,
      colorWarning: c.warning,
      colorError: c.error,
      colorInfo: c.info,
      ...status('Success', c.success),
      ...status('Warning', c.warning),
      ...status('Error', c.error),
      ...status('Info', c.info),
      // Blue *text* uses the link colour: brand blue is too dark to read on dark surfaces.
      colorLink: c.link,
      colorLinkHover: c.link,
      colorPrimaryText: c.link,
      colorPrimaryTextHover: c.link,
      colorBgLayout: c.background,
      colorBgContainer: c.surface,
      colorBgElevated: c.surface,
      colorText: c.text,
      colorTextSecondary: c.textMuted,
      colorBorder: c.border,
      colorBorderSecondary: c.border,
      borderRadius: BRAND.radius,
      fontFamily: BRAND.fontFamily,
      fontFamilyCode: BRAND.fontFamilyMono,
      // 14px body, as in dev-tracker and nestyclass. Tables stay dense via the
      // small component size and padding below, not a smaller base font.
      fontSize: 14,
      controlHeightSM: 28,
    },
    components: {
      // Dense internal tool: tighter tables carry the dispatch board and registers.
      Table: { cellPaddingBlockSM: 8, cellPaddingInlineSM: 10, headerBg: c.background, headerColor: c.textMuted },
    },
  };
}
